import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { setShareMeta } from "../utils/shareMeta";

/**
 * Referral landing — where invite links (pixleyai.com/invite?ref=...) arrive.
 *
 * The ?ref= is captured into localStorage by useReferralCapture the moment
 * this page loads (app-level hook), so by the time the family signs up —
 * even days later, even after the funnel's cross-origin round trip back to
 * /funnel-auth on this origin — the referral is attached automatically.
 *
 * Flow: congrats moment here → CTA into the web funnel (signup → onboarding
 * → trial checkout) → referral completes when the trial/payment starts (both
 * sides get their 150) → final funnel step hands them to the App Store, where
 * they sign into an account that already holds 400 stars.
 */

// The RevenueCat-hosted funnel entry (signup -> onboarding -> trial).
// Set REACT_APP_FUNNEL_URL to the live funnel link; until then the standard
// web signup path is used so this page never dead-ends.
const FUNNEL_URL = process.env.REACT_APP_FUNNEL_URL || "/auth?redirect=/storyverse";

const F = { fontFamily: "'Fredoka', sans-serif" };
const BRAND_BLUE = "#0065F8";
const INK = "#0B1A33";
const SLATE = "#5A6B85";
const GOLD = "#FFCA3A";
const GOLD_DEEP = "#F4A016";

const STARS = [
  { left: "8%", top: "12%", size: 26, opacity: 0.9, rotate: -14 },
  { left: "88%", top: "9%", size: 20, opacity: 0.8, rotate: 12 },
  { left: "94%", top: "38%", size: 14, opacity: 0.55, rotate: -6 },
  { left: "4%", top: "46%", size: 16, opacity: 0.5, rotate: 18 },
  { left: "90%", top: "72%", size: 18, opacity: 0.6, rotate: -12 },
  { left: "7%", top: "80%", size: 14, opacity: 0.5, rotate: 8 },
  { left: "30%", top: "6%", size: 12, opacity: 0.45, rotate: 0 },
  { left: "68%", top: "5%", size: 14, opacity: 0.5, rotate: -20 },
];

const StarGlyph = ({ size, opacity, rotate }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    style={{ opacity, transform: `rotate(${rotate}deg)`, filter: "drop-shadow(0 2px 4px rgba(244,160,22,0.5))" }}
    aria-hidden
  >
    <path
      d="M12 2l2.7 6.3 6.8.6-5.2 4.5 1.6 6.7L12 16.5 6.1 20l1.6-6.7-5.2-4.5 6.8-.6z"
      fill={GOLD}
    />
  </svg>
);

const InvitePage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    setShareMeta({
      title: "You're invited to Pixley",
      description: "Your family starts with 400 stars. Kids make their own cartoons — stories, characters, storybooks.",
    });
    window.scrollTo(0, 0);
    // Overscroll/rubber-banding must reveal sky, never white body.
    const prevBackground = document.body.style.background;
    document.body.style.background = "#C7E4FF";
    return () => {
      document.body.style.background = prevBackground;
    };
  }, []);

  const startFunnel = () => {
    if (/^https?:\/\//i.test(FUNNEL_URL)) {
      window.location.href = FUNNEL_URL;
    } else {
      navigate(FUNNEL_URL);
    }
  };

  return (
    <div className="relative flex min-h-[100dvh] items-center justify-center px-5 py-16" style={F}>
      {/* Fixed sky: fills the viewport regardless of scroll position or the
          mobile URL bar collapsing, so the bottom never turns white. Painted
          INSIDE this page's stacking context (z 0, content above) — a negative
          z-index would drop it behind the app shell's dark background. */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{ background: "linear-gradient(180deg, #388FF8 0%, #6CB2FC 55%, #C7E4FF 100%)" }}
      />

      {STARS.map((star, index) => (
        <span key={index} className="pointer-events-none absolute z-[1]" style={{ left: star.left, top: star.top }}>
          <StarGlyph {...star} />
        </span>
      ))}

      <div className="relative z-[1] w-full max-w-[440px] text-center">
        <div
          className="mx-auto mb-5 inline-block rounded-full px-5 py-2 text-[13px] font-extrabold uppercase tracking-[0.22em] text-white"
          style={{ background: "rgba(255,255,255,0.18)" }}
        >
          A friend invited your family
        </div>

        <h1 className="mb-2 text-[44px] font-black leading-[1.05] text-white" style={{ textShadow: "0 4px 0 rgba(0,101,248,0.45)" }}>
          Welcome to
          <br />
          Pixley!
        </h1>

        <p className="mx-auto mb-7 max-w-[340px] text-[16px] font-semibold text-white/95">
          Your kids make their own cartoons — characters, stories, and storybooks starring them.
        </p>

        {/* The promise card: what the invite is worth. */}
        <div
          className="mb-6 rounded-[26px] bg-white p-6"
          style={{ boxShadow: "0 4px 0 rgba(11,26,51,0.10), 0 18px 40px rgba(11,26,51,0.18)" }}
        >
          <img src="/assets/pixley-logo-520.png" alt="Pixley" className="mx-auto mb-3 h-24 object-contain" />
          <div className="mb-1 text-[15px] font-bold" style={{ color: SLATE }}>
            Your invite is worth
          </div>
          <div
            className="mb-1 text-[52px] font-black leading-none"
            style={{
              background: `linear-gradient(180deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            400 stars
          </div>
          <p className="mx-auto max-w-[300px] text-[13.5px] font-medium" style={{ color: SLATE }}>
            The 250 every new family gets, plus a 150-star bonus from your friend — yours when you start your Pixley trial.
          </p>
        </div>

        <button
          type="button"
          onClick={startFunnel}
          className="mb-3 h-[62px] w-full rounded-full text-[20px] font-bold text-white transition active:scale-[0.97]"
          style={{
            background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #00CAFF 100%)`,
            boxShadow: "0 5px 0 rgba(7,59,142,0.55), 0 14px 30px rgba(0,101,248,0.4)",
          }}
        >
          Start Your Pixley Adventure
        </button>

        <p className="text-[12.5px] font-medium text-white/85">
          Set up takes about two minutes. Your 400 stars land the moment your trial begins.
        </p>
      </div>
    </div>
  );
};

export default InvitePage;
